import { Drawer, Stack, Burger, Group, Container, useMantineColorScheme, Text, Box, ActionIcon } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Link, useLocation } from "@tanstack/react-router"
import {
  IconFileText,
  IconClock,
  IconNews,
  IconGavel,
  IconSpeakerphone,
  IconUsers,
  IconScale,
} from "@tabler/icons-react"
import { ColorSchemeToggle } from "@/components/ui/ColorSchemeToggle"

export function Header() {
  const [opened, { toggle, close }] = useDisclosure(false)
  const location = useLocation()
  const { colorScheme } = useMantineColorScheme()

  const navigation = [
    { href: "/news", label: "Current News", icon: IconNews },
    { href: "/documents", label: "Documents", icon: IconFileText },
    { href: "/timeline", label: "Timeline", icon: IconClock },
    { href: "/legislative", label: "Legislative", icon: IconGavel },
    { href: "/congress", label: "Congress", icon: IconUsers },
    { href: "/action", label: "Take Action", icon: IconSpeakerphone },
  ]

  const isActive = (href: string) => location.pathname === href

  return (
    <>
      <header
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: colorScheme === "dark" ? "rgba(10, 10, 10, 0.95)" : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "saturate(180%) blur(20px)",
          borderBottom: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <Container size="xl">
          <Group justify="space-between" align="center" h={80}>
            {/* Logo */}
            <Link to="/" className="flex items-center gap-4 no-underline group transition-all duration-200">
              <ActionIcon
                variant="subtle"
                size={72}
                radius="md"
                style={{
                  color: "var(--mantine-primary-color-filled)",
                }}
              >
                <IconScale size={40} />
              </ActionIcon>
              <Box className="hidden sm:block">
                <Text size="2.5rem" fw={600} lh={1}>
                  RTEFN
                </Text>
                <Text size="sm" c="dimmed">
                  Release The Epstein Files
                </Text>
              </Box>
            </Link>

            {/* Compact Mobile Navigation - Key Links Only */}
            <Group gap="lg" hiddenFrom="sm">
              <Link
                to="/documents"
                className="text-sm font-medium transition-all duration-200 no-underline"
                style={{
                  color: isActive("/documents") ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
                }}
              >
                Documents
              </Link>
              <Link
                to="/timeline"
                className="text-sm font-medium transition-all duration-200 no-underline"
                style={{
                  color: isActive("/timeline") ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
                }}
              >
                Timeline
              </Link>
            </Group>

            {/* Desktop Navigation - Centered */}
            <Group gap="lg" visibleFrom="sm" style={{ flexGrow: 1, justifyContent: "center" }}>
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-base font-medium transition-all duration-200 no-underline relative group"
                  style={{
                    color: isActive(item.href) ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    {item.icon && <item.icon size={18} />}
                    <span>{item.label}</span>
                  </div>
                  {isActive(item.href) && (
                    <div
                      className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                      style={{ background: "var(--mantine-primary-color-filled)" }}
                    />
                  )}
                  <div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-200"
                    style={{ background: "var(--mantine-primary-color-filled)" }}
                  />
                </Link>
              ))}
            </Group>

            {/* Right side controls */}
            <Group gap="md" visibleFrom="sm">
              <ColorSchemeToggle />
            </Group>

            {/* Mobile Menu Button and Theme Toggle */}
            <Group gap="md" hiddenFrom="sm">
              <ColorSchemeToggle />
              <Burger opened={opened} onClick={toggle} size="sm" color={colorScheme === "dark" ? "white" : "dark"} />
            </Group>
          </Group>
        </Container>
      </header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={opened}
        onClose={close}
        position="right"
        size="320px"
        styles={{
          content: {
            background: colorScheme === "dark" ? "rgba(10, 10, 10, 0.95)" : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "saturate(180%) blur(20px)",
          },
          header: {
            borderBottom:
              colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
            paddingBottom: "1rem",
          },
          title: {
            color: colorScheme === "dark" ? "white" : "#1a1a1a",
            fontWeight: 600,
            fontSize: "1.125rem",
          },
        }}
        title="Navigation"
        overlayProps={{ backgroundOpacity: 0.4, blur: 8 }}
      >
        <Stack gap="sm" mt="lg">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={close}
                className="flex items-center gap-4 px-6 py-4 rounded-xl transition-all duration-200 no-underline group"
                style={{
                  color: isActive(item.href) ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
                  ...(isActive(item.href)
                    ? {
                        background: "var(--mantine-primary-color-light)",
                        borderLeft: "2px solid var(--mantine-primary-color-filled)",
                      }
                    : {}),
                }}
              >
                {Icon && (
                  <Icon
                    size={22}
                    className="transition-colors duration-200"
                    style={{
                      color: isActive(item.href)
                        ? "var(--mantine-primary-color-filled)"
                        : "var(--mantine-color-dimmed)",
                    }}
                  />
                )}
                <span className="font-medium text-base">{item.label}</span>
              </Link>
            )
          })}

          {/* Mobile CTA */}
          <div className="mt-8 px-2">
            <Link to="/action" onClick={close} className="btn-modern btn-primary w-full text-center">
              Take Action →
            </Link>
          </div>
        </Stack>
      </Drawer>
    </>
  )
}
